// frontend/utils/favorites.ts
//
// 즐겨찾기 저장소.
//
// 배경: 즐겨찾기가 `doc-favorites-<userId>` localStorage 키에만 저장돼, 다른 PC·다른 브라우저에서는
//       즐겨찾기가 통째로 사라졌다(캐시 삭제로도 소실). 이제 서버(`/api/favorites`)가 정본이다.
//
// localStorage 를 완전히 버리지 않고 캐시로 남긴 이유:
//   호출부 여러 곳이 렌더 도중 동기적으로 즐겨찾기 맵을 읽는다. 서버 응답을 기다리는 동안
//   즐겨찾기가 잠깐 사라졌다 나타나는 깜빡임을 막으려면 즉시 답할 수 있는 값이 필요하다.
//   따라서 서버 = 정본, localStorage = 마지막으로 본 값의 캐시로 역할을 나눈다.

import { API_URL, authHeaders, authOnlyHeaders } from "./api";

type FavoriteMap = Record<string, boolean>;

const cacheKey = (userId: string) => `doc-favorites-${userId}`;

/** 서버 응답을 기다리지 않고 즉시 읽는 캐시(마지막으로 서버에서 받은 값). */
export function getCachedFavorites(userId?: string): FavoriteMap {
  if (!userId || typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? (JSON.parse(raw) as FavoriteMap) : {};
  } catch {
    return {};
  }
}

function writeCache(userId: string, map: FavoriteMap) {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(map));
  } catch { /* 용량 초과 등 — 캐시일 뿐이라 무시 */ }
}

/**
 * 서버에서 즐겨찾기를 불러온다. 실패하면 캐시를 그대로 돌려준다(오프라인·서버 오류 시 화면 유지).
 * 서버가 비어 있는데 캐시에 값이 있으면, 기존 localStorage 즐겨찾기를 계정으로 1회 이관한다.
 */
export async function loadFavorites(userId?: string, accessToken?: string): Promise<FavoriteMap> {
  if (!userId) return {};
  const cached = getCachedFavorites(userId);

  try {
    const res = await fetch(`${API_URL}/favorites`, { headers: authOnlyHeaders(accessToken) });
    if (!res.ok) return cached;

    let ids: string[] = await res.json();

    // 기존 사용자 이관: 서버가 비어 있고 캐시에만 즐겨찾기가 있는 최초 1회만 수행한다.
    const cachedIds = Object.keys(cached).filter((id) => cached[id]);
    if (ids.length === 0 && cachedIds.length > 0) {
      try {
        const migrated = await fetch(`${API_URL}/favorites/migrate`, {
          method: "POST",
          headers: authHeaders(accessToken),
          body: JSON.stringify({ docIds: cachedIds }),
        });
        if (migrated.ok) {
          const again = await fetch(`${API_URL}/favorites`, { headers: authOnlyHeaders(accessToken) });
          if (again.ok) ids = await again.json();
        }
      } catch { /* 이관 실패해도 조회 결과는 그대로 사용 */ }
    }

    const map: FavoriteMap = {};
    for (const id of ids) map[id] = true;
    writeCache(userId, map);
    return map;
  } catch {
    return cached;
  }
}

/**
 * 즐겨찾기를 켜고 끈다. 캐시를 먼저 갱신(낙관적)한 뒤 서버에 반영한다.
 * @returns 서버 반영 성공 여부. 실패 시 호출부가 UI를 되돌릴 수 있다.
 */
export async function setFavorite(
  docId: string,
  value: boolean,
  userId?: string,
  accessToken?: string,
): Promise<boolean> {
  if (!userId) return false;

  const map = getCachedFavorites(userId);
  if (value) map[docId] = true;
  else delete map[docId];
  writeCache(userId, map);

  try {
    const res = await fetch(`${API_URL}/favorites/${docId}`, {
      method: value ? "PUT" : "DELETE",
      headers: authOnlyHeaders(accessToken),
    });
    if (!res.ok) throw new Error("favorite request failed");
    return true;
  } catch {
    // 서버 반영 실패 — 캐시를 되돌려 서버와 어긋난 상태로 굳지 않게 한다.
    const revert = getCachedFavorites(userId);
    if (value) delete revert[docId];
    else revert[docId] = true;
    writeCache(userId, revert);
    return false;
  }
}
